import {
  InfoCircleOutlined,
  UploadOutlined,
  LoadingOutlined,
  FileExcelOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
} from "@ant-design/icons";
import {
  Button,
  Card,
  Row,
  Spin,
  Result,
  Alert,
  Typography,
  Space,
  Divider,
  message,
} from "antd";
import { UploadRequestOption } from "rc-upload/lib/interface";
import Dragger from "antd/es/upload/Dragger";
import { useState } from "react";
import ResponsiveCol from "common/components/form/ResponsiveCol";
import {
  JsonReportView,
  uploadAndUpdateJsonReportStatusByExcelSatFile,
} from "api/generated";
import { useClient } from "hooks/useClient";
import { FormItem } from "common/components/form/FormItem";
import JsonReportsTable from "routes/jsonReport/components/JsonReportTable";
import useLoggedUser, { UserRole } from "hooks/useLoggedUser";
import UnauthorizedPermission from "common/components/layout/unauthorized/UnautorizedPermissions";
import { handleAxiosError } from "common/helpers/generalUtils";

function UploadJsonSATReport() {
  const [uploading, setUploading] = useState(false);
  const [processExcecuted, setProcessExcecuted] = useState<boolean>(false);
  const { client } = useClient();
  const [jsonReports, setJsonReports] = useState<JsonReportView[]>([]);
  const userInfo = useLoggedUser();

  const validateFileBeforeUpload = (file: File) => {
    const isValidType =
      file.type ===
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" ||
      file.name.endsWith(".xlsx");
    if (!isValidType) {
      message.error("Solo se permiten archivos Excel (.xlsx)");
      return false;
    }
    return true;
  };

  function handleFileUpload(options: UploadRequestOption) {
    const { file, onSuccess, onError } = options;

    setUploading(true);

    // Convert file to base64 for processing
    const reader = new FileReader();
    reader.readAsDataURL(file as Blob);

    reader.onload = () => {
      try {
        const base64Content = (reader.result as string).split(",")[1];
        const binaryString = atob(base64Content);
        const bytes = new Uint8Array(binaryString.length);
        for (let i = 0; i < binaryString.length; i++) {
          bytes[i] = binaryString.charCodeAt(i);
        }
        const excelFile = new Blob([bytes], {
          type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        });

        uploadAndUpdateJsonReportStatusByExcelSatFile({
          client,
          body: { excelFile },
        })
          .then(({ data }) => {
            setJsonReports(data ?? []);
          })
          .catch((err) => {
            handleAxiosError(err, "Error al enviar el documento al servidor");
            setJsonReports([]);
          })
          .finally(() => {
            setUploading(false);
            setProcessExcecuted(true);
          });

        onSuccess?.("Exito");
      } catch (error) {
        const errorObj = error as Error;
        onError?.(errorObj);
      }
    };

    reader.onerror = () => {
      const error = new Error("Error al leer el archivo");
      onError?.(error);
      setUploading(false);
    };
  }

  function resetUpload() {
    setProcessExcecuted(false);
    setJsonReports([]);
  }

  if (userInfo.role !== UserRole.ADMINISTRADOR) {
    return <UnauthorizedPermission />;
  }

  return (
    <div>
      <Row gutter={24}>
        <ResponsiveCol size="xl">
          <Card>
            {/* Instructions Section */}
            <Space direction="vertical" size="middle" style={{ width: "100%" }}>
              <Alert
                message="Instrucciones para el Reporte JSON SAT"
                description={
                  <Space direction="vertical">
                    <Typography.Text>
                      Este proceso marcará los reportes JSON según el estado que
                      está registado en el SAT.
                    </Typography.Text>

                    <Typography.Text>Los estados posibles son:</Typography.Text>

                    <div style={{ marginLeft: "16px" }}>
                      <Typography.Text strong type="success">
                        • Aceptado:{" "}
                      </Typography.Text>
                      <Typography.Text>
                        El SAT ha aceptado el reporte
                      </Typography.Text>
                      <br />
                      <Typography.Text strong type="danger">
                        • Rechazado:{" "}
                      </Typography.Text>
                      <Typography.Text>
                        El SAT ha rechazado el reporte
                      </Typography.Text>
                    </div>

                    <Typography.Text strong>
                      ¿Qué hace el sistema?
                    </Typography.Text>
                    <ul style={{ margin: 0, paddingLeft: "20px" }}>
                      <li>Identifica reportes JSON (mensuales o diarios)</li>
                      <li>
                        Verifica su estado de aceptación/rechazo en el SAT
                      </li>
                      <li>
                        Actualiza automáticamente los estados en la plataforma
                      </li>
                    </ul>
                    <Typography.Text
                      type="secondary"
                      style={{ fontSize: "12px" }}
                    >
                      Puedes subir el mismo archivo múltiples veces - se tomará
                      la versión más reciente
                    </Typography.Text>
                  </Space>
                }
                type="info"
                showIcon
                icon={<InfoCircleOutlined />}
              />

              <Divider />

              {/* Upload Section */}
              <FormItem
                label="Archivo de Reporte SAT (.xlsx)"
                labelAlign="left"
                labelCol={{ span: 24 }}
              >
                <Spin
                  spinning={uploading}
                  indicator={<LoadingOutlined style={{ fontSize: 24 }} spin />}
                >
                  {!processExcecuted && (
                    <Dragger
                      accept=".xlsx, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                      customRequest={handleFileUpload}
                      multiple={false}
                      maxCount={1}
                      beforeUpload={validateFileBeforeUpload}
                      showUploadList={false}
                      disabled={uploading}
                    >
                      <p className="ant-upload-drag-icon">
                        {uploading ? (
                          <LoadingOutlined />
                        ) : (
                          <FileExcelOutlined />
                        )}
                      </p>
                      <p className="ant-upload-text">
                        {uploading
                          ? "Procesando reporte SAT..."
                          : "Haga clic para cargar o arrastre el reporte SAT"}
                      </p>
                      <p className="ant-upload-hint">
                        Solo archivos Excel (.xlsx)
                      </p>
                    </Dragger>
                  )}
                </Spin>
              </FormItem>

              {/* Results Section */}
              {processExcecuted && (
                <Result
                  status={jsonReports.length === 0 ? "error" : "success"}
                  title={
                    jsonReports.length !== 0
                      ? "Reporte SAT procesado exitosamente"
                      : "Ningun registro coincide con los Reportes Json de la plataforma"
                  }
                  extra={[
                    <Button
                      type="primary"
                      key="upload-again"
                      onClick={resetUpload}
                      icon={<UploadOutlined />}
                    >
                      Subir otro archivo
                    </Button>,
                  ]}
                />
              )}
            </Space>
          </Card>
        </ResponsiveCol>
        <ResponsiveCol size="xl">
          {jsonReports.length !== 0 && (
            <div>
              <Divider orientation="left" style={{ borderColor: "#7cb305" }}>
                Reportes Json Actualizados
              </Divider>
              <JsonReportsTable
                data={jsonReports}
                loading={uploading}
                mode="readonly"
                statusConfig={{
                  isSent: (
                    <Space>
                      <CheckCircleOutlined
                        style={{ color: "#52c41a", fontSize: "1.5rem" }}
                      />
                      <span>Aceptado</span>
                    </Space>
                  ),
                  notSent: (
                    <Space>
                      <CloseCircleOutlined
                        style={{ color: "#ff4d4f", fontSize: "1.5rem" }}
                      />
                      <span>Rechazado</span>
                    </Space>
                  ),
                  statusColumnTitle: "Estado SAT",
                }}
              />
            </div>
          )}
        </ResponsiveCol>
      </Row>
      <Divider />
    </div>
  );
}

export default UploadJsonSATReport;
